import { i18nHeaderProvider } from "@vrf/fr-vrf-i18n-react";
import { saveTo, setUserDetails, UserDetails } from "@vrf/fr-vrf-core";
import {
  AUTHORIZATION_ACTION_ITEMS_KEY,
  AUTHORIZATION_MENU_ITEMS_KEY,
  AUTHORIZATION_BELONGING_INFORMATION_KEY,
} from "@vrf/fr-vrf-auth-core";
import {
  AuthClientConfig,
  RestResourceRequester,
  restResourceDownloader as downloader,
  restResourceRequester as requester,
  restResourceUploader as uploader,
  setLocalTimezoneHttpHeader,
  setTestUserIdHttpHeader,
  logout as aadLogout,
} from "@vrf/fr-vrf-rest-resource-client";
import { ENV } from "@vrf/fr-vrf-core";
import { removeUserDetails } from "../util/auth-util";
import { getAuthenticatedUser } from "@vrf/fr-vrf-rest-resource-client";

const API_ENDPOINT = ENV("REACT_APP_REST_API_HOST");
const API_DEFAULT_TIMEOUT = Number(ENV("REACT_APP_REST_API_DEFAULT_TIMEOUT"));
const DOWNLOAD_DEFAULT_TIMEOUT = Number(
  ENV("REACT_APP_REST_DOWNLOAD_DEFAULT_TIMEOUT"),
);
const UPLOAD_DEFAULT_TIMEOUT = Number(
  ENV("REACT_APP_REST_UPLOAD_DEFAULT_TIMEOUT"),
);
const ENABLE_DEV_LOGIN = ENV("REACT_APP_ENABLE_DEV_LOGIN") === "true";

const API_DEFAULT_HEADERS = {
  "Content-Type": "application/json",
  "X-Requested-With": "XMLHttpRequest",
};

type Headers = Record<string, string>;

const buildHeaders = (
  ...parts: Array<Record<string, string | undefined> | undefined>
): Headers => {
  const merged: Headers = {};
  for (const part of parts) {
    if (!part) continue;
    for (const [key, value] of Object.entries(part)) {
      if (typeof value === "string") {
        merged[key] = value;
      }
    }
  }
  return merged;
};

export const headerProvider = <REQ>(_request: REQ): Headers =>
  buildHeaders(
    API_DEFAULT_HEADERS,
    i18nHeaderProvider(),
    setLocalTimezoneHttpHeader(),
    setTestUserIdHttpHeader(ENABLE_DEV_LOGIN),
  );

export const uploadHeaderProvider = <REQ>(_request: REQ): Headers =>
  buildHeaders(
    { "X-Requested-With": "XMLHttpRequest" },
    i18nHeaderProvider(),
    setLocalTimezoneHttpHeader(),
    setTestUserIdHttpHeader(ENABLE_DEV_LOGIN),
  );

export const restResourceRequester = <REQ, RES>(
  method: "GET" | "POST" | "DELETE" | "PUT" | "HEAD",
  urlProvider: (request: REQ) => string,
  bodyProvider?: (request: REQ) => BodyInit,
): RestResourceRequester<REQ, RES> => {
  const baseRequester = requester<REQ, RES>(
    method,
    (request: REQ) => `${API_ENDPOINT}/${urlProvider(request)}`,
    {
      bodyProvider: bodyProvider,
      headerProvider: headerProvider,
      timeout: API_DEFAULT_TIMEOUT,
    },
  );
  return baseRequester;
};

export const restResourceDownloader = <REQ>(
  method: "GET" | "POST",
  urlProvider: (request: REQ) => string,
  bodyProvider?: (request: REQ) => BodyInit,
): RestResourceRequester<REQ, void> => {
  const baseDownloader = downloader<REQ>(
    method,
    (request: REQ) => `${API_ENDPOINT}/${urlProvider(request)}`,
    {
      bodyProvider: bodyProvider,
      headerProvider: headerProvider,
      downloadFilenameProvider: (filename) => {
        const [matched, basename, timestamp, timeZone, extension] =
          WITH_TIMESTAMP_SUFFIX.exec(filename) ?? [];
        if (!matched) {
          return filename;
        }
        if (timestamp) {
          return timestamp === "__"
            ? basename + "." + extension
            : timeZone
              ? basename + timestamp + timeZone + "." + extension
              : basename + timestamp + "." + extension;
        }
        return basename + "." + extension;
      },
      timeout: DOWNLOAD_DEFAULT_TIMEOUT,
    },
  );
  return baseDownloader;
};

export const restResourceUploader = <REQ extends Record<string, any>, RES>(
  urlProvider: (request: REQ) => string,
): RestResourceRequester<REQ, RES> => {
  const baseUploader = uploader<REQ, RES>(
    (request: REQ) => `${API_ENDPOINT}/${urlProvider(request)}`,
    {
      headerProvider: uploadHeaderProvider,
      timeout: UPLOAD_DEFAULT_TIMEOUT,
    },
  );
  return baseUploader;
};

const WITH_TIMESTAMP_SUFFIX: RegExp = /^(.*?)(_\d{14}|__)([A-Z]{3})?\.([^.]+)$/;

export const AAD_CONFIG: AuthClientConfig = {
  authEndpoint: ENV("REACT_APP_AAD_AUTH_ENDPOINT"),
  unauthorizedCallback: () => {
    removeUserDetails();
  },
  authLogoutEndpoint: ENV("REACT_APP_AAD_AUTH_LOGOUT_ENDPOINT"),
  logoutCallback: () => {
    saveTo("session", AUTHORIZATION_ACTION_ITEMS_KEY, null);
    saveTo("session", AUTHORIZATION_MENU_ITEMS_KEY, null);
    saveTo("session", AUTHORIZATION_BELONGING_INFORMATION_KEY, null);
    removeUserDetails();
  },
};

export const fetchAuthenticatedUser = async (): Promise<UserDetails | null> => {
  try {
    const user = await getAuthenticatedUser(AAD_CONFIG, {
      headerProvider: () => headerProvider(undefined),
    });
    setUserDetails(user);
    return user;
  } catch {
    removeUserDetails();
    return null;
  }
};

export const logout = async () => {
  if (!ENABLE_DEV_LOGIN) {
    await aadLogout(AAD_CONFIG);
  }
};
